import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  X, Palette, FileText, Image as ImageIcon,
  Check, Info, Type, ChevronDown, ChevronUp,
  Sparkles, Wand2, Loader2, Film, Video, Zap, Crown, Layers, Link2
} from 'lucide-react';
import ContentWizardStep from '../../../components/contents/wizard/ContentWizardStep';
import CreationLoadingScreen from '../../../components/contents/wizard/CreationLoadingScreen';
import CreditsExhaustedModal from '../../../components/contents/CreditsExhaustedModal';
import { contentService } from '../../../services/ContentService';
import { aiGenerationService } from '../../../services/aiGenerationService';
import { useBrandIdentity } from '../../../contexts/BrandIdentityContext';
import { stripeService } from '../../../services/StripeService';
import { nichesService } from '../../../services/NichesService';

// ============================================
// CONSTANTES
// ============================================

const AVAILABLE_FONTS = [
  { display: 'Montserrat', value: 'montserrat' },
  { display: 'Inter', value: 'inter' },
  { display: 'Poppins', value: 'poppins' },
  { display: 'Roboto', value: 'roboto' },
  { display: 'Open Sans', value: 'open sans' },
  { display: 'Lato', value: 'lato' },
  { display: 'Raleway', value: 'raleway' },
  { display: 'Nunito', value: 'nunito' },
  { display: 'Oswald', value: 'oswald' },
  { display: 'Playfair Display', value: 'playfair display' },
  { display: 'Bangers', value: 'bangers' },
  { display: 'Lobster', value: 'lobster' },
  { display: 'Pacifico', value: 'pacifico' },
  { display: 'Space Mono', value: 'space mono' },
];

const FONT_CATEGORIES = {
  'Modernas': ['inter', 'poppins', 'montserrat'],
  'Clássicas': ['roboto', 'open sans', 'lato'],
  'Display': ['oswald', 'raleway', 'nunito'],
  'Decorativas': ['bangers', 'lobster', 'pacifico'],
  'Elegantes': ['playfair display'],
  'Mono': ['space mono'],
};

const QUICK_ACTIONS = [
  { id: 'vender_produto', label: 'Vender', emoji: '🛒', requires_product: true },
  { id: 'anunciar_promocao', label: 'Promoção', emoji: '🏷️', requires_product: false },
  { id: 'lancar_produto', label: 'Lançamento', emoji: '🚀', requires_product: true },
  { id: 'engajar_audiencia', label: 'Engajar', emoji: '💬', requires_product: false },
  { id: 'mostrar_bastidores', label: 'Bastidores', emoji: '🎬', requires_product: false },
  { id: 'dica_educativa', label: 'Dica', emoji: '💡', requires_product: false },
];

// ============================================
// OPÇÕES DE FORMATO
// ============================================
const FORMAT_OPTIONS = [
  {
    value: 'post',
    label: 'Post',
    icon: FileText,
    color: 'from-green-500 to-emerald-500',
    description: 'Posts estáticos para o feed',
    badge: null
  },
  {
    value: 'reels',
    label: 'Reels',
    icon: Film,
    color: 'from-purple-500 to-pink-500',
    description: 'Conteúdo para Reels/Stories',
    badge: '+ Vídeo'
  },
  {
    value: 'carousel',
    label: 'Carrossel',
    icon: Layers,
    color: 'from-purple-500 to-indigo-500',
    description: 'Sequência de 2 a 5 slides',
    badge: 'NOVO'
  }
];

const ContentWizard = () => {
  const navigate = useNavigate();
  const { brandIdentity, colors: brandColors, fonts: brandFonts, logoUrl: brandLogoUrl } = useBrandIdentity();
  
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [batchId, setBatchId] = useState(null);
  
  // ✅ NOVO: Estado para formato (post/reels/carousel)
  const [contentFormat, setContentFormat] = useState(null);

  // Estado para tamanho do carrossel (2-5)
  const [carouselSize, setCarouselSize] = useState(5);

  // ✅ Fase 4: Dimensão da imagem
  //   portrait  → 1080x1350 ("Feed Novo", PADRÃO) — gpt-image
  //   square    → 1080x1080 (Feed) — provedores tradicionais (Creatomate/Templated/React)
  //   story     → 1080x1920 (Stories) — gpt-image
  // Reels sempre vai pra story (vertical 9:16); usuario nao escolhe.
  const [imageDimensions, setImageDimensions] = useState('portrait');

  // ✅ NOVO: Estado para plano do usuário
  const [userPlan, setUserPlan] = useState('free');
  // Créditos: null = ainda carregando (não bloqueia nada até saber)
  const [creditsBalance, setCreditsBalance] = useState(null);
  const [creditCosts, setCreditCosts] = useState({ image: 1, video: 5 });
  const [isLoadingPlan, setIsLoadingPlan] = useState(true);

  // Modais
  const [showPromptsModal, setShowPromptsModal] = useState(false);

  // Estado para ações rápidas
  const [showQuickActionInput, setShowQuickActionInput] = useState(null);
  const [quickActionProduct, setQuickActionProduct] = useState('');

  // Estado para imagens da biblioteca
  const [selectedLibraryImages, setSelectedLibraryImages] = useState([]);

  // Estado para dropdowns
  const [showPrimaryColorPresets, setShowPrimaryColorPresets] = useState(false);
  const [showSecondaryColorPresets, setShowSecondaryColorPresets] = useState(false);
  const [showPrimaryFontDropdown, setShowPrimaryFontDropdown] = useState(false);
  const [showSecondaryFontDropdown, setShowSecondaryFontDropdown] = useState(false);

  // Estado para geração de prompts via IA
  const [isGeneratingPrompts, setIsGeneratingPrompts] = useState(false);
  const [generatedPrompts, setGeneratedPrompts] = useState([]);
  const [promptIdea, setPromptIdea] = useState('');

  const [showLimitModal, setShowLimitModal] = useState(false);
  const [limitMessage, setLimitMessage] = useState('');

  // Estados para cards expansivos
  const [isPromptAIOpen, setIsPromptAIOpen] = useState(true);
  const [isLinkSectionOpen, setIsLinkSectionOpen] = useState(false);

  // Estados para link de trend
  const [trendUrl, setTrendUrl] = useState('');
  const [isExtractingTrend, setIsExtractingTrend] = useState(false);

  // Nichos
  const [niches, setNiches] = useState([]);
  const [selectedNicheId, setSelectedNicheId] = useState(null);
  const [isLoadingNiches, setIsLoadingNiches] = useState(false);

  // Estado do formulário
  const [formData, setFormData] = useState({
    user_prompt: '',
    selected_content_types: ['marketing'],
    use_brand_colors: true,
    use_custom_colors: false,
    custom_colors: {
      primary: '#F59E0B',
      secondary: '#000000'
    },
    custom_fonts: {
      primary: 'inter',
      secondary: 'poppins'
    },
    use_brand_logo: false,
    use_business_info: true,
    use_library_images: false,
    generation_settings: {
      dall_e_3_count: 0,
      image_size: '1024x1024',
      image_style: 'vivid',
      image_quality: 'hd',
      flux_11_count: 0,
      flux_11_size: '1024x1024',
      flux_2_pro_count: 0,
      flux_2_pro_size: '1024x1024',
      flux_kontext_pro_count: 0,
      flux_kontext_pro_size: '1024x1024',
      nano_banana_count: 0,
      sora_count: 0,
      veo_31_count: 0,
      video_duration: 8,
      video_width: 1280,
      video_height: 720,
      placid_count: null
    },
    enabled_models: {
      dall_e_3: false,
      flux_11: false,
      flux_2_pro: false,
      flux_kontext_pro: false,
      nano_banana: false,
      sora: false,
      veo_31: false,
      placid: true
    },
    content_types: ['marketing']
  });

  // Dados derivados
  const imageLibrary = brandIdentity?.image_library || [];


  // ============================================
  // CARREGAR PLANO DO USUÁRIO
  // ============================================
  
  useEffect(() => {
    const loadUserPlan = async () => {
      setIsLoadingPlan(true);
      try {
        const response = await stripeService.getSubscriptionStatus();
        if (response.success && response.data?.plan) {
          setUserPlan(response.data.plan);
          // Saldo de créditos: usado pra avisar ANTES de tentar gerar
          const credits = response.data?.usage?.credits;
          if (credits && typeof credits.balance === 'number') {
            setCreditsBalance(credits.balance);
            setCreditCosts(credits.costs || { image: 1, video: 5 });
          }
        }
      } catch (error) {
        console.warn('Erro ao carregar plano:', error);
        setUserPlan('free');
      } finally {
        setIsLoadingPlan(false);
      }
    };

    loadUserPlan();
  }, []);

  // Carregar nichos
  useEffect(() => {
    const loadNiches = async () => {
      setIsLoadingNiches(true);
      try {
        const response = await nichesService.listNiches();
        setNiches(response.niches || []);
      } catch (error) {
        console.warn('Erro ao carregar nichos:', error);
        setNiches([]);
      } finally {
        setIsLoadingNiches(false);
      }
    };
    loadNiches();
  }, []);

  const handleSelectNiche = (niche) => {
    if (!niche) {
      setSelectedNicheId(null);
      return;
    }
    setSelectedNicheId(niche.id);

    // Aplica cores/fontes padrão do nicho quando o cliente não tem brand_identity
    const hasBrand = !!(brandColors?.primary || brandFonts?.primary);
    if (!hasBrand && (niche.default_colors?.length || niche.default_fonts?.primary)) {
      setFormData(prev => ({
        ...prev,
        use_brand_colors: false,
        use_custom_colors: true,
        custom_colors: {
          primary: niche.default_colors?.[0] || prev.custom_colors.primary,
          secondary: niche.default_colors?.[1] || prev.custom_colors.secondary,
        },
        custom_fonts: {
          primary: niche.default_fonts?.primary || prev.custom_fonts.primary,
          secondary: niche.default_fonts?.secondary || prev.custom_fonts.secondary,
        }
      }));
    }
  };

  // ============================================
  // HANDLERS - Navegação
  // ============================================

  const handleNext = () => {
    if (currentStep < 2) {
      setCurrentStep(currentStep + 1);
    } else {
      handleSubmit();
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  // ✅ NOVO: Selecionar formato
  const selectFormat = (format) => {
    setContentFormat(format);
  };


  // ============================================
  // HANDLERS - Cores e Fontes
  // ============================================

  const handleToggleBrandColors = (enabled) => {
    setFormData(prev => ({
      ...prev,
      use_brand_colors: enabled,
      use_custom_colors: enabled ? false : prev.use_custom_colors
    }));
  };

  const handleToggleCustomColors = (enabled) => {
    setFormData(prev => ({
      ...prev,
      use_custom_colors: enabled,
      use_brand_colors: enabled ? false : prev.use_brand_colors
    }));
  };

  const handleCustomColorChange = (type, value) => {
    let hex = value;
    if (!hex.startsWith('#')) {
      hex = '#' + hex;
    }
    if (/^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/.test(hex)) {
      setFormData(prev => ({
        ...prev,
        custom_colors: { ...prev.custom_colors, [type]: hex }
      }));
    }
  };

  const handleCustomFontChange = (type, value) => {
    setFormData(prev => ({
      ...prev,
      custom_fonts: { ...prev.custom_fonts, [type]: value }
    }));
    if (type === 'primary') {
      setShowPrimaryFontDropdown(false);
    } else {
      setShowSecondaryFontDropdown(false);
    }
  };

  // ============================================
  // HANDLERS - Imagens da Biblioteca
  // ============================================

  const toggleImageSelection = (image) => {
    setSelectedLibraryImages(prev => {
      const isSelected = prev.some(img => img.id === image.id);
      if (isSelected) {
        return prev.filter(img => img.id !== image.id);
      } else {
        return [...prev, image];
      }
    });
  };

  const isImageSelected = (imageId) => {
    return selectedLibraryImages.some(img => img.id === imageId);
  };

  const handleToggleLibraryImages = (enabled) => {
    setFormData(prev => ({ ...prev, use_library_images: enabled }));
    if (!enabled) {
      setSelectedLibraryImages([]);
    }
  };

  // ============================================
  // HANDLERS - Geração de Prompts via IA
  // ============================================

  const handleGeneratePromptsFromIdea = async () => {
    if (!promptIdea.trim() || promptIdea.length < 5) {
      alert('Digite uma ideia com pelo menos 5 caracteres');
      return;
    }

    setIsGeneratingPrompts(true);
    setGeneratedPrompts([]);

    try {
      const contentType = formData.selected_content_types[0] || 'marketing';
      const prompts = await aiGenerationService.generateContentPrompts(promptIdea, contentType);
      setGeneratedPrompts(prompts);
      setShowPromptsModal(true);
    } catch (error) {
      console.error('Erro ao gerar prompts:', error);
      alert('Erro ao gerar prompts. Tente novamente.');
    } finally {
      setIsGeneratingPrompts(false);
    }
  };

  const handleQuickAction = async (action) => {
    if (action.requires_product) {
      setShowQuickActionInput(action.id);
      return;
    }
    await executeQuickAction(action.id, '');
  };

  const executeQuickAction = async (actionId, product) => {
    setIsGeneratingPrompts(true);
    setGeneratedPrompts([]);
    setShowQuickActionInput(null);
    setQuickActionProduct('');

    try {
      const contentType = formData.selected_content_types[0] || 'marketing';
      const prompts = await aiGenerationService.generateQuickPrompts(actionId, product, contentType);
      setGeneratedPrompts(prompts);
      setShowPromptsModal(true);
    } catch (error) {
      console.error('Erro ao gerar prompts rápidos:', error);
      alert('Erro ao gerar prompts. Tente novamente.');
    } finally {
      setIsGeneratingPrompts(false);
    }
  };

  const handleSelectPrompt = (prompt) => {
    setFormData(prev => ({ ...prev, user_prompt: prompt.text }));
    setShowPromptsModal(false);
    setGeneratedPrompts([]);
  };

  // Handler para extrair conteúdo de link e gerar prompt
  const handleExtractTrendLink = async () => {
    if (!trendUrl.trim()) return;

    setIsExtractingTrend(true);
    try {
      const contentType = formData.selected_content_types?.[0] || 'marketing';
      const prompt = await aiGenerationService.generatePromptFromLink(trendUrl.trim(), contentType);
      if (prompt) {
        setFormData(prev => ({ ...prev, user_prompt: prompt }));
        setTrendUrl('');
      }
    } catch (error) {
      console.error('Erro ao extrair link:', error);
      alert(error.response?.data?.detail || 'Erro ao processar link. Verifique se a URL é válida.');
    } finally {
      setIsExtractingTrend(false);
    }
  };

  // ============================================
  // CÁLCULOS E VALIDAÇÕES
  // ============================================

  // ✅ NOVO: Calcular o que será gerado baseado no formato e plano
  const getGenerationConfig = () => {
    if (contentFormat === 'post') {
      // POST: Usa configuração padrão do admin (placid_count: null = usa config do banco)
      return {
        enabled_models: {
          dall_e_3: false,
          flux_11: false,
          flux_2_pro: false,
          flux_kontext_pro: false,
          nano_banana: false,
          sora: false,
          veo_31: false,
          placid: true
        },
        generation_settings: {
          ...formData.generation_settings,
          placid_count: null, // Usa config do admin (padrão 3)
          sora_count: 0,
          veo_31_count: 0
        }
      };
    } else if (contentFormat === 'reels') {
      // REELS: vídeo HyperFrames (motor próprio HTML+GSAP→MP4, 9:16 animado).
      // O agente escolhe a direção de arte pelo briefing e usa logo/imagens
      // enviadas. Disponível a todos os planos — custa 5 créditos por vídeo.
      const config = {
        enabled_models: {
          dall_e_3: false,
          flux_11: false,
          flux_2_pro: false,
          flux_kontext_pro: false,
          nano_banana: false,
          sora: false,
          veo_31: false,
          placid: false,
          hyperframes: true
        },
        generation_settings: {
          ...formData.generation_settings,
          placid_count: 0,
          sora_count: 0,
          veo_31_count: 0,
          hyperframes_count: 1,
          video_width: 1080,
          video_height: 1920,
          video_duration_seconds: 10,
        }
      };
      return config;
    }
    
    if (contentFormat === 'carousel') {
      return {
        enabled_models: { creatomate: true },
        generation_settings: { creatomate_count: carouselSize }
      };
    }

    return {
      enabled_models: formData.enabled_models,
      generation_settings: formData.generation_settings
    };
  };

  // ✅ NOVO: Obter resumo do que será gerado
  const getGenerationSummary = () => {
    if (contentFormat === 'carousel') {
      return {
        posts: `Carrossel (${carouselSize} slides)`,
        videos: null,
        description: `Será gerado um carrossel com ${carouselSize} slides via Creatomate`
      };
    } else if (contentFormat === 'post') {
      return {
        posts: 'Posts (config. padrão)',
        videos: null,
        description: 'Serão gerados posts com templates profissionais'
      };
    } else if (contentFormat === 'reels') {
      return {
        posts: null,
        videos: '1 vídeo animado (Reels)',
        description: 'Será gerado 1 Reels animado com a identidade da sua marca (5 créditos)',
        upgradeMessage: userPlan === 'free'
          ? 'Vídeos custam 5 créditos — assine um plano para gerar Reels'
          : null
      };
    }
    return null;
  };

  const calculateTotalContents = () => {
    if (contentFormat === 'carousel') return carouselSize;

    const config = getGenerationConfig();
    let total = 0;

    if (config.enabled_models.placid) {
      const placidCount = config.generation_settings.placid_count;
      if (placidCount === null || placidCount === undefined) {
        total += 3; // Padrão do admin
      } else {
        total += placidCount;
      }
    }
    
    if (config.enabled_models.sora) {
      total += config.generation_settings.sora_count || 0;
    }

    if (config.enabled_models.veo_31) {
      total += config.generation_settings.veo_31_count || 0;
    }

    if (config.enabled_models.hyperframes) {
      total += config.generation_settings.hyperframes_count || 1;
    }

    return total;
  };

  const getContentType = () => {
    const config = getGenerationConfig();
    const hasVideo = config.enabled_models.sora || config.enabled_models.veo_31 || config.enabled_models.hyperframes;
    const hasImage = config.enabled_models.placid;
    
    if (hasVideo && hasImage) return 'mixed';
    if (hasVideo) return 'video';
    return 'image';
  };

  const isStepValid = () => {
    switch (currentStep) {
      case 1:
        return contentFormat !== null;
      case 2:
        const hasPrompt = formData.user_prompt.trim().length >= 10;
        return hasPrompt;
      default:
        return true;
    }
  };

  // ============================================
  // UTILITÁRIOS
  // ============================================

  const getFontDisplay = (fontValue) => {
    if (!fontValue) return 'Inter';
    const found = AVAILABLE_FONTS.find(f => f.value.toLowerCase() === fontValue.toLowerCase());
    return found ? found.display : fontValue;
  };

  const closeAllDropdowns = () => {
    setShowPrimaryColorPresets(false);
    setShowSecondaryColorPresets(false);
    setShowPrimaryFontDropdown(false);
    setShowSecondaryFontDropdown(false);
  };

  // ============================================
  // BUILD FINAL PROMPT
  // ============================================

  const buildFinalPrompt = () => {
    let finalPrompt = formData.user_prompt;
    
    // Adicionar contexto do formato
    if (contentFormat === 'carousel') {
      finalPrompt += `\n\n🎠 FORMATO: CARROSSEL INSTAGRAM (${carouselSize} slides)`;
      finalPrompt += `\nEste conteudo sera usado para um carrossel no Instagram com ${carouselSize} slides. Mantenha coerencia visual entre os slides.`;
    } else if (contentFormat === 'reels') {
      finalPrompt += `\n\n📱 FORMATO: REELS/STORIES`;
      finalPrompt += `\nEste conteúdo será usado para Reels/Stories. Considere o formato vertical (9:16) e elementos visuais impactantes.`;
    }
    
    // Cores e Fontes
    if (formData.use_brand_colors && brandColors) {
      const primaryColor = brandColors.primary || '#F59E0B';
      const secondaryColor = brandColors.secondary || '#000000';
      const primaryFont = brandFonts?.primary || 'inter';
      const secondaryFont = brandFonts?.secondary || 'poppins';
      
      finalPrompt += `\n\n🎨 IDENTIDADE VISUAL DA MARCA:`;
      finalPrompt += `\n• Cor primária: ${primaryColor}`;
      finalPrompt += `\n• Cor secundária: ${secondaryColor}`;
      finalPrompt += `\n• Fonte principal (títulos): ${primaryFont}`;
      finalPrompt += `\n• Fonte secundária (textos): ${secondaryFont}`;
      finalPrompt += `\nUse estas cores e fontes nos templates para manter a identidade visual da marca.`;
      
    } else if (formData.use_custom_colors) {
      const primaryColor = formData.custom_colors.primary;
      const secondaryColor = formData.custom_colors.secondary;
      const primaryFont = formData.custom_fonts.primary;
      const secondaryFont = formData.custom_fonts.secondary;
      
      finalPrompt += `\n\n🎨 CORES E FONTES PERSONALIZADAS:`;
      finalPrompt += `\n• Cor primária: ${primaryColor}`;
      finalPrompt += `\n• Cor secundária: ${secondaryColor}`;
      finalPrompt += `\n• Fonte principal (títulos): ${primaryFont}`;
      finalPrompt += `\n• Fonte secundária (textos): ${secondaryFont}`;
      finalPrompt += `\nUse estas cores e fontes nos templates conforme especificado.`;
    }
    
    // Logo
    if (formData.use_brand_logo && brandLogoUrl) {
      finalPrompt += `\n\n🏷️ LOGO DA MARCA:`;
      finalPrompt += `\nO usuário adicionou a logo da marca: ${brandLogoUrl}`;
      finalPrompt += `\n⚠️ IMPORTANTE: Evite usar a logo em templates com imagem de fundo (background) para não distorcer/alargar a imagem.`;
      finalPrompt += `\nRecomendação: Use a logo em templates que possuem espaço dedicado para logo, preferencialmente em áreas de aproximadamente 250x250 pixels ou slots específicos para marca.`;
    }
    
    // Info do negócio
    if (formData.use_business_info && brandIdentity?.about_business) {
      finalPrompt += `\n\n📋 SOBRE O NEGÓCIO:`;
      finalPrompt += `\n${brandIdentity.about_business}`;
    }

    // Imagens da biblioteca
    if (formData.use_library_images && selectedLibraryImages.length > 0) {
      finalPrompt += `\n\n🖼️ BIBLIOTECA DE IMAGENS DO USUÁRIO:`;
      finalPrompt += `\nO usuário anexou ${selectedLibraryImages.length} imagen${selectedLibraryImages.length > 1 ? 's' : ''} da sua biblioteca para utilizar nos templates conforme fizer sentido:`;
      
      selectedLibraryImages.forEach((img, index) => {
        finalPrompt += `\n\n${index + 1}. ${img.image_url}`;
        if (img.caption) {
          finalPrompt += `\n   Descrição: ${img.caption}`;
        }
      });
      
      finalPrompt += `\n\n💡 Utilize estas imagens nos templates quando forem relevantes para o conteúdo. Não é obrigatório usar todas - escolha as que melhor se adequam a cada template.`;
    }
    
    return finalPrompt;
  };

  // ============================================
  // SUBMIT
  // ============================================

  // Créditos necessários pro que está configurado agora
  const getRequiredCredits = () => {
    const config = getGenerationConfig();
    const em = config.enabled_models || {};
    const gs = config.generation_settings || {};
    const videos = (em.hyperframes ? (gs.hyperframes_count || 1) : 0)
      + (em.sora ? (gs.sora_count || 0) : 0)
      + (em.veo_31 ? (gs.veo_31_count || 0) : 0);
    const images = Math.max(0, calculateTotalContents() - videos);
    return images * (creditCosts.image || 1) + videos * (creditCosts.video || 5);
  };

  const hasEnoughCredits = creditsBalance === null || creditsBalance >= getRequiredCredits();

  const handleSubmit = async () => {
    // GUARD: sem créditos suficientes, avisa ANTES de tentar gerar
    // (evita a tela de loading que ficava girando quando o backend recusava)
    if (creditsBalance !== null) {
      const needed = getRequiredCredits();
      if (creditsBalance < needed) {
        setLimitMessage(
          creditsBalance === 0
            ? 'Seus créditos acabaram. Escolha um plano para continuar criando conteúdos.'
            : `Você tem ${creditsBalance} crédito(s) e esta criação precisa de ${needed}. Escolha um plano maior para continuar.`
        );
        setShowLimitModal(true);
        return;
      }
    }
    return handleSubmitInternal();
  };

  const handleSubmitInternal = async () => {
    const config = getGenerationConfig();
    
    setIsSubmitting(true);

    try {
      const submitData = {
        user_prompt: buildFinalPrompt(),
        enabled_models: config.enabled_models,
        generation_settings: config.generation_settings,
        content_types: formData.content_types,
        selected_library_images: formData.use_library_images ? selectedLibraryImages : [],
        brand_config: {
          use_brand_colors: formData.use_brand_colors,
          use_custom_colors: formData.use_custom_colors,
          custom_colors: formData.custom_colors,
          custom_fonts: formData.custom_fonts,
          use_brand_logo: formData.use_brand_logo
        }
      };

      // Carousel: adicionar campos especificos
      if (contentFormat === 'carousel') {
        submitData.batch_type = 'carousel';
        submitData.carousel_size = carouselSize;
      }

      // ✅ Fase 4: dimensao da imagem (square/portrait/story)
      // - reels SEMPRE story (vertical)
      // - post/carousel: usa o que o usuario escolheu (default square)
      const effectiveDimensions = contentFormat === 'reels' ? 'story' : imageDimensions;
      submitData.generation_settings = {
        ...submitData.generation_settings,
        dimensions: effectiveDimensions,
      };
      // Non-square cai pro gpt-image (provedores tradicionais nao suportam bem)
      if (effectiveDimensions !== 'square') {
        submitData.prefers_gpt_image = true;
      }

      // Nicho selecionado (opcional)
      if (selectedNicheId) {
        submitData.niche_id = selectedNicheId;
      }

      console.log('📤 Enviando para backend:', submitData);

      const response = await contentService.createBatch(submitData);
      setBatchId(response.data.batch_id);
      
    } catch (error) {
      console.error('Erro ao criar batch:', error);
      
      // ✅ Tratar erro 403 (créditos insuficientes)
      if (error.response?.status === 403) {
        const detail = error.response?.data?.detail;
        const remaining = typeof detail === 'object' ? detail?.remaining : null;
        const message = remaining === 0
          ? 'Seus créditos acabaram. Escolha um plano para continuar criando conteúdos.'
          : (typeof detail === 'string'
              ? detail
              : detail?.message || 'Você não tem créditos suficientes para esta criação.');
        setLimitMessage(message);
        setShowLimitModal(true);
        if (typeof remaining === 'number') setCreditsBalance(remaining);
      } else {
        const errorMsg = typeof error.response?.data?.detail === 'string'
          ? error.response?.data?.detail
          : error.response?.data?.detail?.message || error.message;
        alert(`Erro ao criar lote: ${errorMsg}`);
      }
      
      setIsSubmitting(false);
    }
  };

  const handleLoadingComplete = () => {
    if (batchId) {
      navigate(`/contents/batch/${batchId}`);
    }
  };

  // ============================================
  // COMPONENTE: Font Selector
  // ============================================

  const FontSelector = ({ type, value, isOpen, onToggle, onSelect }) => {
    const currentFont = AVAILABLE_FONTS.find(f => f.value === value) || AVAILABLE_FONTS.find(f => f.value === 'inter');
    
    return (
      <div className="relative">
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            closeAllDropdowns();
            onToggle();
          }}
          className="w-full flex items-center justify-between px-4 py-3 bg-white border border-gray-200 rounded-xl hover:border-amber-300 transition-all"
        >
          <div className="flex items-center gap-3">
            <Type className="w-4 h-4 text-gray-500" />
            <span className="font-medium text-gray-900">{currentFont?.display || 'Inter'}</span>
          </div>
          <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </button>
        
        {isOpen && (
          <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-200 rounded-xl shadow-xl z-30 max-h-60 overflow-y-auto">
            {Object.entries(FONT_CATEGORIES).map(([category, fontValues]) => (
              <div key={category}>
                <div className="px-4 py-2 bg-gray-50 border-b border-gray-100 sticky top-0">
                  <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{category}</span>
                </div>
                {fontValues.map(fontValue => {
                  const font = AVAILABLE_FONTS.find(f => f.value === fontValue);
                  if (!font) return null;
                  
                  return (
                    <button
                      key={font.value}
                      type="button"
                      onClick={() => onSelect(type, font.value)}
                      className={`w-full flex items-center justify-between px-4 py-2.5 hover:bg-amber-50 transition-colors text-left ${
                        value === font.value ? 'bg-amber-50 border-l-2 border-amber-500' : ''
                      }`}
                    >
                      <span className="text-gray-900">{font.display}</span>
                      {value === font.value && <Check className="w-4 h-4 text-amber-600" />}
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  // ============================================
  // COMPONENTE: Resumo da Geração
  // ============================================

  const GenerationSummary = () => {
    const summary = getGenerationSummary();
    if (!summary) return null;

    const getPlanIcon = () => {
      switch (userPlan) {
        case 'pro': return <Sparkles className="w-4 h-4" />;
        case 'max': return <Crown className="w-4 h-4" />;
        case 'basic': return <Zap className="w-4 h-4" />;
        default: return null;
      }
    };

    const getPlanColor = () => {
      switch (userPlan) {
        case 'pro': return 'bg-amber-100 text-amber-700';
        case 'max': return 'bg-purple-100 text-purple-700';
        case 'basic': return 'bg-blue-100 text-blue-700';
        default: return 'bg-gray-100 text-gray-700';
      }
    };

    return (
      <div className={`rounded-2xl p-5 border-2 ${contentFormat === 'reels' ? 'bg-gradient-to-br from-purple-50 to-pink-50 border-purple-200' : 'bg-gradient-to-br from-green-50 to-emerald-50 border-green-200'}`}>
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-semibold text-gray-900 flex items-center gap-2">
            {contentFormat === 'reels' ? <Film className="w-5 h-5 text-purple-600" /> : <FileText className="w-5 h-5 text-green-600" />}
            Resumo da Geração
          </h4>
          <span className={`px-2 py-1 rounded-full text-xs font-semibold flex items-center gap-1 ${getPlanColor()}`}>
            {getPlanIcon()}
            Plano {userPlan.charAt(0).toUpperCase() + userPlan.slice(1)}
          </span>
        </div>
        
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Check className="w-4 h-4 text-green-600" />
            <span className="text-gray-700">{summary.posts}</span>
          </div>
          
          {summary.videos ? (
            <div className="flex items-center gap-2">
              <Check className="w-4 h-4 text-green-600" />
              <span className="text-gray-700">{summary.videos}</span>
              <Video className="w-4 h-4 text-purple-500" />
            </div>
          ) : contentFormat === 'reels' && (
            <div className="flex items-center gap-2">
              <X className="w-4 h-4 text-gray-400" />
              <span className="text-gray-400">Sem vídeo no plano atual</span>
            </div>
          )}
        </div>

        <p className="mt-3 text-sm text-gray-600">{summary.description}</p>

        {/* Custo x saldo — informativo (o aviso de falta de crédito é um popup) */}
        {creditsBalance !== null && (
          <div className={`mt-3 flex items-center gap-2 text-sm ${hasEnoughCredits ? 'text-gray-600' : 'text-amber-700 font-medium'}`}>
            <Sparkles className="w-4 h-4 text-amber-500" />
            <span>
              Custo: <strong>{getRequiredCredits()}</strong> crédito(s) ·
              Saldo: <strong>{creditsBalance}</strong>
            </span>
          </div>
        )}

        {summary.upgradeMessage && (
          <div className="mt-3 p-3 bg-white/70 rounded-xl border border-amber-200">
            <p className="text-sm text-amber-700 flex items-center gap-2">
              <Sparkles className="w-4 h-4" />
              {summary.upgradeMessage}
            </p>
            <button
              type="button"
              onClick={() => navigate('/plans')}
              className="mt-2 text-sm font-medium text-amber-600 hover:text-amber-700 underline"
            >
              Ver planos →
            </button>
          </div>
        )}
      </div>
    );
  };

  // ============================================
  // LOADING SCREEN
  // ============================================

  if (isSubmitting) {
    return (
      <CreationLoadingScreen 
        contentType={getContentType()}
        onComplete={handleLoadingComplete}
      />
    );
  }

  // ============================================
  // RENDER
  // ============================================

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-orange-50 py-12 px-4" onClick={closeAllDropdowns}>
      <div className="max-w-4xl mx-auto">
        
        {/* ============================================ */}
        {/* STEP 1: Formato (Post / Reels) */}
        {/* ============================================ */}
        {currentStep === 1 && (
          <ContentWizardStep
            currentStep={1}
            totalSteps={2}
            title="O que você quer criar?"
            subtitle="Escolha o formato do conteúdo"
            onNext={handleNext}
            nextDisabled={!isStepValid()}
            showBack={false}
          >
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {FORMAT_OPTIONS.map(format => {
                const IconComponent = format.icon;
                const selected = contentFormat === format.value;
                const hasVideo = format.value === 'reels' && (userPlan === 'pro' || userPlan === 'max');
                const showBadge = format.badge && (format.value === 'carousel' || hasVideo);

                return (
                  <button
                    key={format.value}
                    type="button"
                    onClick={() => selectFormat(format.value)}
                    className={`
                      relative p-8 rounded-3xl border-2 transition-all duration-300 text-left
                      transform hover:scale-[1.02] active:scale-[0.98]
                      ${selected
                        ? `bg-gradient-to-br ${format.color} text-white border-transparent shadow-xl`
                        : 'bg-white border-gray-200 hover:border-amber-200 hover:shadow-lg'
                      }
                    `}
                  >
                    {/* Badge */}
                    {showBadge && (
                      <span className={`absolute top-4 right-4 px-3 py-1 rounded-full text-xs font-bold ${selected ? 'bg-white/20 text-white' : 'bg-purple-100 text-purple-700'}`}>
                        {format.badge}
                      </span>
                    )}

                    <div className={`
                      w-20 h-20 rounded-2xl flex items-center justify-center mb-6
                      ${selected ? 'bg-white/20' : 'bg-gradient-to-br from-amber-50 to-orange-50'}
                    `}>
                      <IconComponent className={`w-10 h-10 ${selected ? 'text-white' : format.value === 'post' ? 'text-green-600' : 'text-purple-600'}`} />
                    </div>

                    <h3 className={`text-2xl font-bold mb-2 ${selected ? 'text-white' : 'text-gray-900'}`}>
                      {format.label}
                    </h3>

                    <p className={`text-base ${selected ? 'text-white/80' : 'text-gray-500'}`}>
                      {format.description}
                    </p>

                    {/* Info sobre vídeo para Reels */}
                    {format.value === 'reels' && (
                      <div className={`mt-4 pt-4 border-t ${selected ? 'border-white/20' : 'border-gray-100'}`}>
                        <div className="flex items-center gap-2">
                          <Video className={`w-4 h-4 ${selected ? 'text-white/80' : 'text-gray-400'}`} />
                          <span className={`text-sm ${selected ? 'text-white/80' : 'text-gray-500'}`}>
                            {userPlan === 'free'
                              ? 'Vídeo animado (5 créditos) — disponível nos planos pagos'
                              : 'Vídeo animado com a identidade da sua marca (5 créditos)'}
                          </span>
                        </div>
                      </div>
                    )}

                    {/* Info sobre carrossel */}
                    {format.value === 'carousel' && (
                      <div className={`mt-4 pt-4 border-t ${selected ? 'border-white/20' : 'border-gray-100'}`}>
                        <div className="flex items-center gap-2">
                          <Layers className={`w-4 h-4 ${selected ? 'text-white/80' : 'text-gray-400'}`} />
                          <span className={`text-sm ${selected ? 'text-white/80' : 'text-gray-500'}`}>
                            Layout consistente via Creatomate
                          </span>
                        </div>
                      </div>
                    )}

                    {/* Checkmark quando selecionado */}
                    {selected && (
                      <div className="absolute top-4 left-4 w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                        <Check className="w-5 h-5 text-white" />
                      </div>
                    )}
                  </button>
                );
              })}
            </div>

            {/* Seletor de tamanho do carrossel */}
            {contentFormat === 'carousel' && (
              <div className="mt-8 p-6 bg-purple-50 rounded-2xl border border-purple-100">
                <h4 className="text-sm font-semibold text-purple-900 mb-3">Quantos slides?</h4>
                <div className="flex gap-3">
                  {[2, 3, 4, 5].map(size => (
                    <button
                      key={size}
                      type="button"
                      onClick={() => setCarouselSize(size)}
                      className={`
                        flex-1 py-3 rounded-xl border-2 font-bold text-lg transition-all
                        ${carouselSize === size
                          ? 'border-purple-500 bg-purple-500 text-white shadow-md'
                          : 'border-purple-200 bg-white text-purple-700 hover:border-purple-300'
                        }
                      `}
                    >
                      {size}
                    </button>
                  ))}
                </div>
                <p className="mt-2 text-xs text-purple-600">
                  O agente vai gerar {carouselSize} slides com layout consistente
                </p>
              </div>
            )}

            {/* ✅ Fase 4: Seletor de dimensao da imagem (post + carrossel) */}
            {(contentFormat === 'post' || contentFormat === 'carousel') && (
              <div className="mt-8 p-6 bg-emerald-50 rounded-2xl border border-emerald-100">
                <h4 className="text-sm font-semibold text-emerald-900 mb-3">Tamanho da imagem</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {[
                    { value: 'portrait', label: 'Feed Novo', size: '1080x1350', desc: 'Vertical otimizado feed (padrão)' },
                    { value: 'square', label: 'Feed', size: '1080x1080', desc: 'Quadrado clássico' },
                    { value: 'story', label: 'Stories', size: '1080x1920', desc: 'Vertical 9:16' },
                  ].map(opt => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setImageDimensions(opt.value)}
                      className={`
                        p-4 rounded-xl border-2 text-left transition-all
                        ${imageDimensions === opt.value
                          ? 'border-emerald-500 bg-emerald-500 text-white shadow-md'
                          : 'border-emerald-200 bg-white text-emerald-700 hover:border-emerald-300'
                        }
                      `}
                    >
                      <div className="font-bold text-base">{opt.label}</div>
                      <div className={`text-xs ${imageDimensions === opt.value ? 'text-white/80' : 'text-emerald-500'} font-mono`}>{opt.size}</div>
                      <div className={`text-xs mt-1 ${imageDimensions === opt.value ? 'text-white/70' : 'text-emerald-600/70'}`}>{opt.desc}</div>
                    </button>
                  ))}
                </div>
                {imageDimensions !== 'square' && (
                  <p className="mt-3 text-xs text-emerald-700">
                    ✨ Formato vertical usa gpt-image-2 (OpenAI) — renderiza tipografia e logos com qualidade superior.
                  </p>
                )}
              </div>
            )}
          </ContentWizardStep>
        )}

        {/* ============================================ */}
        {/* STEP 2: Detalhes do Conteúdo */}
        {/* ============================================ */}
        {currentStep === 2 && (
          <ContentWizardStep
            currentStep={2}
            totalSteps={2}
            title="Descreva seu conteúdo"
            subtitle="Quanto mais detalhes, melhor o resultado"
            onBack={handleBack}
            onNext={handleSubmit}
            nextDisabled={!isStepValid()}
            nextLabel="Gerar Conteúdos!"
          >
            <div className="space-y-6">

              {/* ============================================ */}
              {/* SEÇÃO: NICHO (opcional) */}
              {/* ============================================ */}
              {(niches.length > 0 || isLoadingNiches) && (
                <div className="bg-white rounded-2xl border border-gray-200 p-5">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h4 className="font-semibold text-gray-900">Para qual área é esse post?</h4>
                      <p className="text-xs text-gray-500 mt-0.5">Selecione um nicho para herdar estilo visual e tom — ou continue como Genérico.</p>
                    </div>
                    {selectedNicheId && (
                      <button
                        type="button"
                        onClick={() => handleSelectNiche(null)}
                        className="text-xs text-gray-500 hover:text-gray-900 underline"
                      >
                        Limpar
                      </button>
                    )}
                  </div>

                  {isLoadingNiches ? (
                    <div className="flex items-center gap-2 text-sm text-gray-500 py-4">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Carregando nichos...
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                      {/* Card Genérico */}
                      <button
                        type="button"
                        onClick={() => handleSelectNiche(null)}
                        className={`p-3 rounded-xl border-2 text-left transition-all ${
                          !selectedNicheId
                            ? 'border-amber-500 bg-amber-50 shadow-sm'
                            : 'border-gray-200 bg-white hover:border-gray-300'
                        }`}
                      >
                        <div className="text-2xl mb-1">🎨</div>
                        <div className="font-semibold text-sm text-gray-900">Genérico</div>
                        <div className="text-[10px] text-gray-500 mt-0.5">Sem estilo direcionado</div>
                      </button>

                      {niches.map(niche => {
                        const isSelected = selectedNicheId === niche.id;
                        const previews = niche.preview_samples || [];
                        return (
                          <button
                            key={niche.id}
                            type="button"
                            onClick={() => handleSelectNiche(niche)}
                            className={`p-3 rounded-xl border-2 text-left transition-all ${
                              isSelected
                                ? 'border-amber-500 bg-amber-50 shadow-sm'
                                : 'border-gray-200 bg-white hover:border-gray-300'
                            }`}
                          >
                            <div className="text-2xl mb-1">{niche.icon || '🏷️'}</div>
                            <div className="font-semibold text-sm text-gray-900 truncate">{niche.name}</div>
                            {previews.length > 0 && (
                              <div className="mt-2 grid grid-cols-3 gap-1">
                                {previews.slice(0, 3).map((p, idx) => (
                                  p.image_url ? (
                                    <img
                                      key={idx}
                                      src={p.image_url}
                                      alt=""
                                      className="w-full aspect-square object-cover rounded"
                                    />
                                  ) : (
                                    <div key={idx} className="w-full aspect-square bg-gray-100 rounded" />
                                  )
                                ))}
                              </div>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {/* ============================================ */}
              {/* SEÇÃO: GERAÇÃO DE PROMPT COM IA (Expansivo) */}
              {/* ============================================ */}
              <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-2xl border border-purple-200 overflow-hidden">
                <button
                  type="button"
                  onClick={() => setIsPromptAIOpen(!isPromptAIOpen)}
                  className="w-full p-5 flex items-center justify-between hover:bg-purple-50/50 transition-colors"
                >
                  <h4 className="font-semibold text-gray-900 flex items-center gap-2">
                    <Wand2 className="w-5 h-5 text-purple-600" />
                    Gerar prompt com IA
                  </h4>
                  {isPromptAIOpen ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
                </button>

                {isPromptAIOpen && (
                  <div className="px-5 pb-5 space-y-4">
                    {/* Ações Rápidas */}
                    <div>
                      <p className="text-sm text-gray-600 mb-2">Ações rápidas:</p>
                      <div className="flex flex-wrap gap-2">
                        {QUICK_ACTIONS.map(action => (
                          <button
                            key={action.id}
                            type="button"
                            onClick={() => handleQuickAction(action)}
                            disabled={isGeneratingPrompts}
                            className="px-3 py-2 bg-white border border-gray-200 rounded-xl text-sm font-medium hover:border-purple-300 hover:bg-purple-50 transition-all flex items-center gap-1.5 disabled:opacity-50"
                          >
                            <span>{action.label}</span>
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Input para ação que requer produto */}
                    {showQuickActionInput && (
                      <div className="p-3 bg-white rounded-xl border border-purple-200 animate-fadeIn">
                        <p className="text-sm text-gray-700 mb-2">Qual produto ou serviço você quer destacar?</p>
                        <div className="flex gap-2">
                          <input
                            type="text"
                            value={quickActionProduct}
                            onChange={(e) => setQuickActionProduct(e.target.value)}
                            placeholder="Ex: Hambúrguer artesanal, Curso de inglês..."
                            className="flex-1 px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-100 focus:border-purple-400"
                          />
                          <button
                            type="button"
                            onClick={() => executeQuickAction(showQuickActionInput, quickActionProduct)}
                            disabled={!quickActionProduct.trim()}
                            className="px-4 py-2 bg-purple-600 text-white rounded-xl font-medium hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            Gerar
                          </button>
                          <button
                            type="button"
                            onClick={() => { setShowQuickActionInput(null); setQuickActionProduct(''); }}
                            className="px-3 py-2 text-gray-500 hover:bg-gray-100 rounded-xl"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Ou digite sua ideia */}
                    <div>
                      <p className="text-sm text-gray-600 mb-2">Ou descreva sua ideia:</p>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={promptIdea}
                          onChange={(e) => setPromptIdea(e.target.value)}
                          placeholder="Ex: Quero vender meu curso de culinária com 30% de desconto..."
                          className="flex-1 px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-100 focus:border-purple-400"
                          onKeyPress={(e) => { if (e.key === 'Enter') handleGeneratePromptsFromIdea(); }}
                        />
                        <button
                          type="button"
                          onClick={handleGeneratePromptsFromIdea}
                          disabled={isGeneratingPrompts || promptIdea.length < 5}
                          className="px-4 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-xl font-medium hover:from-purple-700 hover:to-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                        >
                          {isGeneratingPrompts ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                          Gerar
                        </button>
                      </div>
                    </div>

                    {isGeneratingPrompts && (
                      <div className="flex items-center justify-center gap-2 text-purple-600">
                        <Loader2 className="w-5 h-5 animate-spin" />
                        <span className="text-sm font-medium">Gerando prompts incríveis...</span>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* ============================================ */}
              {/* SEÇÃO: GERAR A PARTIR DE LINK (Expansivo) */}
              {/* ============================================ */}
              <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-2xl border border-blue-200 overflow-hidden">
                <button
                  type="button"
                  onClick={() => setIsLinkSectionOpen(!isLinkSectionOpen)}
                  className="w-full p-5 flex items-center justify-between hover:bg-blue-50/50 transition-colors"
                >
                  <h4 className="font-semibold text-gray-900 flex items-center gap-2">
                    <Link2 className="w-5 h-5 text-blue-600" />
                    Gerar a partir de link
                    <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs font-semibold">Novo</span>
                  </h4>
                  {isLinkSectionOpen ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
                </button>

                {isLinkSectionOpen && (
                  <div className="px-5 pb-5 space-y-3">
                    <p className="text-sm text-gray-600">
                      Cole o link de uma notícia, trend ou artigo e a IA vai transformar em um prompt estratégico.
                    </p>

                    <div className="flex gap-2">
                      <input
                        type="url"
                        value={trendUrl}
                        onChange={(e) => setTrendUrl(e.target.value)}
                        placeholder="https://exemplo.com/noticia-ou-trend..."
                        className="flex-1 px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-100 focus:border-blue-400"
                        onKeyPress={(e) => { if (e.key === 'Enter') handleExtractTrendLink(); }}
                      />
                      <button
                        type="button"
                        onClick={handleExtractTrendLink}
                        disabled={isExtractingTrend || !trendUrl.trim()}
                        className="px-4 py-3 bg-gradient-to-r from-blue-600 to-cyan-600 text-white rounded-xl font-medium hover:from-blue-700 hover:to-cyan-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                      >
                        {isExtractingTrend ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />}
                        Gerar
                      </button>
                    </div>

                    {isExtractingTrend && (
                      <div className="flex items-center justify-center gap-2 text-blue-600">
                        <Loader2 className="w-5 h-5 animate-spin" />
                        <span className="text-sm font-medium">Extraindo conteúdo e gerando prompt...</span>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Campo de Prompt */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Seu prompt</label>
                <textarea
                  value={formData.user_prompt}
                  onChange={(e) => setFormData(prev => ({ ...prev, user_prompt: e.target.value }))}
                  placeholder="Ex: Post promocional de Black Friday com 50% de desconto em todos os produtos. Destaque o senso de urgência e economia. Visual moderno com cores vibrantes."
                  className="w-full px-5 py-4 border-2 border-gray-200 rounded-2xl focus:outline-none focus:ring-4 focus:ring-amber-100 focus:border-amber-500 text-base resize-none transition-all"
                  rows={5}
                />
                <div className="flex items-center justify-between mt-2">
                  <p className="text-sm text-gray-500">{formData.user_prompt.length}/500 caracteres</p>
                  <p className="text-sm text-gray-400">Mínimo 10 caracteres</p>
                </div>
              </div>

              {/* ============================================ */}
              {/* SEÇÃO: CORES E FONTES */}
              {/* ============================================ */}
              <div className="bg-gray-50 rounded-2xl p-5 space-y-4">
                <h4 className="font-semibold text-gray-900 flex items-center gap-2">
                  <Palette className="w-5 h-5 text-amber-600" />
                  Cores e Fontes
                </h4>
                
                {/* Toggle: Usar cores/fontes da marca */}
                <label className="flex items-center gap-3 cursor-pointer group">
                  <div className="relative">
                    <input
                      type="checkbox"
                      checked={formData.use_brand_colors}
                      onChange={(e) => handleToggleBrandColors(e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-300 rounded-full peer-checked:bg-amber-500 transition-colors"></div>
                    <div className="absolute left-0.5 top-0.5 w-5 h-5 bg-white rounded-full shadow peer-checked:translate-x-5 transition-transform"></div>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-900">Usar cores e fontes da marca</span>
                      {brandColors && (
                        <div className="flex items-center gap-1">
                          <div className="w-4 h-4 rounded border border-gray-300" style={{ backgroundColor: brandColors.primary }} />
                          <div className="w-4 h-4 rounded border border-gray-300" style={{ backgroundColor: brandColors.secondary }} />
                        </div>
                      )}
                    </div>
                    <p className="text-sm text-gray-500">Aplica as cores e fontes da sua identidade visual</p>
                  </div>
                </label>

                {/* Toggle: Usar cores/fontes personalizadas */}
                <label className="flex items-center gap-3 cursor-pointer group">
                  <div className="relative">
                    <input
                      type="checkbox"
                      checked={formData.use_custom_colors}
                      onChange={(e) => handleToggleCustomColors(e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-300 rounded-full peer-checked:bg-amber-500 transition-colors"></div>
                    <div className="absolute left-0.5 top-0.5 w-5 h-5 bg-white rounded-full shadow peer-checked:translate-x-5 transition-transform"></div>
                  </div>
                  <div>
                    <span className="font-medium text-gray-900">Usar cores e fontes personalizadas</span>
                    <p className="text-sm text-gray-500">Define cores e fontes específicas para este conteúdo</p>
                  </div>
                </label>

                {/* Seletor de Cores e Fontes Personalizadas */}
                {formData.use_custom_colors && (
                  <div className="mt-4 p-4 bg-white rounded-xl border border-amber-200 space-y-4 animate-fadeIn">
                    <p className="text-sm font-medium text-amber-700 flex items-center gap-2">
                      <Sparkles className="w-4 h-4" />
                      Configure suas cores e fontes personalizadas
                    </p>
                    
                    {/* Cores */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Cor Primária</label>
                        <div className="flex items-center gap-2">
                          <input
                            type="color"
                            value={formData.custom_colors.primary}
                            onChange={(e) => setFormData(prev => ({ ...prev, custom_colors: { ...prev.custom_colors, primary: e.target.value } }))}
                            className="w-10 h-10 rounded-lg border border-gray-200 cursor-pointer p-1"
                          />
                          <input
                            type="text"
                            value={formData.custom_colors.primary.toUpperCase()}
                            onChange={(e) => handleCustomColorChange('primary', e.target.value)}
                            className="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-amber-100 focus:border-amber-400 font-mono"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Cor Secundária</label>
                        <div className="flex items-center gap-2">
                          <input
                            type="color"
                            value={formData.custom_colors.secondary}
                            onChange={(e) => setFormData(prev => ({ ...prev, custom_colors: { ...prev.custom_colors, secondary: e.target.value } }))}
                            className="w-10 h-10 rounded-lg border border-gray-200 cursor-pointer p-1"
                          />
                          <input
                            type="text"
                            value={formData.custom_colors.secondary.toUpperCase()}
                            onChange={(e) => handleCustomColorChange('secondary', e.target.value)}
                            className="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-amber-100 focus:border-amber-400 font-mono"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Fontes */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Fonte Principal</label>
                        <FontSelector
                          type="primary"
                          value={formData.custom_fonts.primary}
                          isOpen={showPrimaryFontDropdown}
                          onToggle={() => setShowPrimaryFontDropdown(!showPrimaryFontDropdown)}
                          onSelect={handleCustomFontChange}
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Fonte Secundária</label>
                        <FontSelector
                          type="secondary"
                          value={formData.custom_fonts.secondary}
                          isOpen={showSecondaryFontDropdown}
                          onToggle={() => setShowSecondaryFontDropdown(!showSecondaryFontDropdown)}
                          onSelect={handleCustomFontChange}
                        />
                      </div>
                    </div>

                    {/* Preview */}
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <p className="text-xs text-gray-500 mb-2">Preview</p>
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg border" style={{ backgroundColor: formData.custom_colors.primary }} />
                        <div className="w-8 h-8 rounded-lg border" style={{ backgroundColor: formData.custom_colors.secondary }} />
                        <span className="text-sm text-gray-700">{getFontDisplay(formData.custom_fonts.primary)} + {getFontDisplay(formData.custom_fonts.secondary)}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* ============================================ */}
              {/* SEÇÃO: LOGO */}
              {/* ============================================ */}
              {brandLogoUrl && (
                <div className="bg-gray-50 rounded-2xl p-5">
                  <label className="flex items-center gap-3 cursor-pointer group">
                    <div className="relative">
                      <input
                        type="checkbox"
                        checked={formData.use_brand_logo}
                        onChange={(e) => setFormData(prev => ({ ...prev, use_brand_logo: e.target.checked }))}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-300 rounded-full peer-checked:bg-amber-500 transition-colors"></div>
                      <div className="absolute left-0.5 top-0.5 w-5 h-5 bg-white rounded-full shadow peer-checked:translate-x-5 transition-transform"></div>
                    </div>
                    <div className="flex items-center gap-3 flex-1">
                      <img src={brandLogoUrl} alt="Logo" className="w-10 h-10 rounded-lg object-contain border border-gray-200 bg-white" />
                      <div>
                        <span className="font-medium text-gray-900">Usar logo da marca</span>
                        <p className="text-sm text-gray-500">Adiciona a logo em templates compatíveis (evita backgrounds)</p>
                      </div>
                    </div>
                  </label>
                </div>
              )}

              {/* ============================================ */}
              {/* SEÇÃO: OUTRAS OPÇÕES */}
              {/* ============================================ */}
              <div className="bg-gray-50 rounded-2xl p-5 space-y-4">
                <h4 className="font-semibold text-gray-900 flex items-center gap-2">
                  <Info className="w-5 h-5 text-amber-600" />
                  Personalização adicional
                </h4>

                {/* Checkbox: Usar info do negócio */}
                <label className="flex items-center gap-3 cursor-pointer group">
                  <div className="relative">
                    <input
                      type="checkbox"
                      checked={formData.use_business_info}
                      onChange={(e) => setFormData(prev => ({ ...prev, use_business_info: e.target.checked }))}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-300 rounded-full peer-checked:bg-amber-500 transition-colors"></div>
                    <div className="absolute left-0.5 top-0.5 w-5 h-5 bg-white rounded-full shadow peer-checked:translate-x-5 transition-transform"></div>
                  </div>
                  <div>
                    <span className="font-medium text-gray-900">Usar informações do negócio</span>
                    <p className="text-sm text-gray-500">Inclui o contexto "Sobre meu negócio" do seu perfil</p>
                  </div>
                </label>

                {/* Checkbox: Usar imagens da biblioteca */}
                <label className="flex items-center gap-3 cursor-pointer group">
                  <div className="relative">
                    <input
                      type="checkbox"
                      checked={formData.use_library_images}
                      onChange={(e) => handleToggleLibraryImages(e.target.checked)}
                      className="sr-only peer"
                      disabled={imageLibrary.length === 0}
                    />
                    <div className={`w-11 h-6 rounded-full peer-checked:bg-amber-500 transition-colors ${imageLibrary.length === 0 ? 'bg-gray-200 cursor-not-allowed' : 'bg-gray-300'}`}></div>
                    <div className="absolute left-0.5 top-0.5 w-5 h-5 bg-white rounded-full shadow peer-checked:translate-x-5 transition-transform"></div>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className={`font-medium ${imageLibrary.length === 0 ? 'text-gray-400' : 'text-gray-900'}`}>Usar imagens da minha biblioteca</span>
                      {imageLibrary.length > 0 && (
                        <span className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full text-xs font-semibold">{imageLibrary.length} disponíveis</span>
                      )}
                    </div>
                    <p className={`text-sm ${imageLibrary.length === 0 ? 'text-gray-400' : 'text-gray-500'}`}>
                      {imageLibrary.length === 0 ? 'Adicione imagens na sua Identidade Visual para usar aqui' : 'Selecione imagens para o agente usar nos posts'}
                    </p>
                  </div>
                </label>
              </div>

              {/* Seção de Seleção de Imagens da Biblioteca */}
              {formData.use_library_images && imageLibrary.length > 0 && (
                <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl p-5 border-2 border-amber-200">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="font-semibold text-gray-900 flex items-center gap-2">
                      <ImageIcon className="w-5 h-5 text-amber-600" />
                      Selecione as imagens
                    </h4>
                    {selectedLibraryImages.length > 0 && (
                      <span className="px-3 py-1 bg-amber-500 text-white rounded-full text-sm font-semibold">
                        {selectedLibraryImages.length} selecionada{selectedLibraryImages.length > 1 ? 's' : ''}
                      </span>
                    )}
                  </div>

                  <div className="grid grid-cols-4 sm:grid-cols-5 md:grid-cols-6 gap-3">
                    {imageLibrary.map((image) => {
                      const selected = isImageSelected(image.id);
                      return (
                        <button
                          key={image.id}
                          type="button"
                          onClick={() => toggleImageSelection(image)}
                          className={`relative aspect-square rounded-xl overflow-hidden border-3 transition-all ${selected ? 'border-amber-500 ring-2 ring-amber-300 scale-105' : 'border-gray-200 hover:border-amber-300'}`}
                        >
                          <img
                            src={image.image_url}
                            alt={image.caption || 'Imagem da biblioteca'}
                            className="w-full h-full object-cover"
                            onError={(e) => { e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="%23ccc"><rect width="24" height="24"/></svg>'; }}
                          />
                          <div className={`absolute inset-0 flex items-center justify-center transition-all ${selected ? 'bg-amber-500/30' : 'bg-black/0 hover:bg-black/20'}`}>
                            {selected && (
                              <div className="w-8 h-8 bg-amber-500 rounded-full flex items-center justify-center shadow-lg">
                                <Check className="w-5 h-5 text-white" />
                              </div>
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>

                  {selectedLibraryImages.length > 0 && (
                    <div className="mt-4 p-3 bg-white/80 rounded-xl border border-amber-200">
                      <div className="flex items-start gap-2">
                        <Info className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                        <div className="text-sm text-gray-700">
                          <p className="font-medium text-gray-900 mb-1">{selectedLibraryImages.length} imagem{selectedLibraryImages.length > 1 ? 'ns' : ''} selecionada{selectedLibraryImages.length > 1 ? 's' : ''}</p>
                          <p className="text-gray-600">O agente usará estas imagens nos templates quando fizerem sentido para o conteúdo.</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </ContentWizardStep>
        )}
      </div>

      {/* ============================================ */}
      {/* MODAL: Prompts Gerados */}
      {/* ============================================ */}
      {showPromptsModal && generatedPrompts.length > 0 && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 animate-fadeIn">
          <div className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full overflow-hidden">
            <div className="p-6 border-b border-gray-100 flex items-center justify-between bg-gradient-to-r from-purple-50 to-indigo-50">
              <div>
                <h3 className="text-xl font-bold text-gray-900">Prompts Gerados</h3>
                <p className="text-sm text-gray-600 mt-1">Escolha um prompt ou edite depois</p>
              </div>
              <button
                type="button"
                onClick={() => { setShowPromptsModal(false); setGeneratedPrompts([]); }}
                className="p-2 hover:bg-white/50 rounded-xl transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            
            <div className="p-6 space-y-4 max-h-[60vh] overflow-y-auto">
              {generatedPrompts.map((prompt, index) => (
                <button
                  key={index}
                  type="button"
                  onClick={() => handleSelectPrompt(prompt)}
                  className="w-full p-4 bg-white border-2 border-gray-200 rounded-2xl text-left hover:border-purple-300 hover:bg-purple-50 transition-all group"
                >
                  <div className="flex items-start gap-3">
                    <span className="w-8 h-8 bg-purple-100 text-purple-700 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">{index + 1}</span>
                    <div className="flex-1">
                      <p className="text-gray-900 font-medium mb-1">{prompt.text}</p>
                      <p className="text-xs text-gray-500">Foco: {prompt.focus}</p>
                    </div>
                    <Check className="w-5 h-5 text-purple-600 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                </button>
              ))}
            </div>

            <div className="p-6 border-t border-gray-100 bg-gray-50">
              <button
                type="button"
                onClick={() => { setShowPromptsModal(false); setGeneratedPrompts([]); }}
                className="w-full py-3 text-gray-600 hover:text-gray-900 transition-colors"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ============================================ */}
      {/* MODAL: Limite do Plano */}
      {/* ============================================ */}
      <CreditsExhaustedModal
        open={showLimitModal}
        balance={creditsBalance}
        required={getRequiredCredits()}
        message={limitMessage}
        onClose={() => setShowLimitModal(false)}
      />
    </div>
  );
};

export default ContentWizard;